// Time formatting + drift math for the "X ago" freshness display.
//
// The dashboard shows three different time strings:
//   - formatTs(raw)            — formats a server-side timestamp string
//                                into the user's locale (sv-SE).
//   - formatDuration(ms)       — compact "Ns / Nm / Nh / Nd / Nweek" form
//                                used for "seen X ago".
//   - driftSrv2Local(ta, tb)   — pure math: time-between-two-timestamps,
//                                used to compute per-terminal clock drift.
//   - lastSeenFor(t)           — projects the terminal's last-seen onto
//                                the browser's wall clock using TWO drifts
//                                (per-terminal + per-page-load).
//
// lastSeenFor reads globalData.drift_srv2local in the original. We pass
// it in as a parameter so the function is pure.

import type { Terminal } from '../types';

export function formatTs(raw: unknown): string {
  if (!raw) return '—';
  // Date objects (e.g. from lastSeenFor) stringify to a form whose spaces
  // are not ISO separators — convert to ISO first so we don't render the
  // raw "Sat Jul 11 2026 10:30:00 GMT+0000 (...)" toString.
  const d = raw instanceof Date ? raw : new Date(String(raw).trim().replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(raw);
  return d.toLocaleString('sv');
}

// Compact human duration formatter. See admin/index.php's original
// function for the full spec — the implementation here is a 1:1 port,
// with explicit edge-case handling for "exactly 24h" → "1d".
export function formatDuration(ms: unknown): string {
  if (ms == null) return '—';
  const n = Number(ms);
  if (!isFinite(n) || n < 0) return '—';

  const SEC = 1000;
  const MIN = 60 * SEC;
  const HOUR = 60 * MIN;
  const DAY = 24 * HOUR;
  const WEEK = 7 * DAY;

  if (n < MIN) {
    return Math.floor(n / SEC) + 's';
  }
  if (n < HOUR) {
    return Math.floor(n / MIN) + 'm';
  }
  if (n < DAY) {
    const h = Math.floor(n / HOUR);
    const rm = n - h * HOUR;
    const mm = Math.floor(rm / MIN);
    return mm === 0 ? `${h}h` : `${h}h${mm}`;
  }
  if (n < 2 * DAY) {
    const rh = n - DAY;
    const h = Math.floor(rh / HOUR);
    const rm = rh - h * HOUR;
    const mm = Math.floor(rm / MIN);
    if (h === 0 && mm === 0) return '1d';
    let out = `1d${h}h`;
    if (mm !== 0) out += mm;
    return out;
  }
  if (n < 7 * DAY) {
    const d = Math.floor(n / DAY);
    const rh = n - d * DAY;
    const h = Math.floor(rh / HOUR);
    const rm = rh - h * HOUR;
    const mm = Math.floor(rm / MIN);
    let out = `${d}d${h}h`;
    if (mm !== 0) out += mm;
    return out;
  }
  if (n <= 4 * WEEK) {
    const w = Math.floor(n / WEEK);
    return `${w}${w === 1 ? 'week' : 'weeks'}`;
  }
  return Math.floor(n / DAY) + 'd';
}

// Drift between two timestamp strings: ms(tb) - ms(ta). Returns null
// if either is unparseable. Positive = tb is in the future relative to
// ta (i.e. ta is "that many ms ago" from tb's perspective).
export function driftSrv2Local(ta: unknown, tb: unknown): number | null {
  const ms = new Date(String(tb).replace(' ', 'T')).getTime()
           - new Date(String(ta).replace(' ', 'T')).getTime();
  return isNaN(ms) ? null : ms;
}

// Project a terminal's last-seen onto the browser's wall clock.
//
//   browser_now_terminal  =  ts_spot + t.drift + drift_srv2local
//                         =  ts_spot + (ts_spot - ts_serv) + (nowMs - data.srvtime)
//
// The two offsets are computed elsewhere and passed in — this function
// stays pure so it's trivially testable.
export function lastSeenFor(t: Terminal | null | undefined, driftSrv2LocalMs: number | null | undefined): Date | null {
  if (!t || !t.ts_spot) return null;
  const offsetMs = (t.drift || 0) + (driftSrv2LocalMs || 0);
  const ms = new Date(String(t.ts_spot).replace(' ', 'T')).getTime() + offsetMs;
  return isNaN(ms) ? null : new Date(ms);
}